import React, { useState, useEffect } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Input, Modal, Spin, Upload, message, Badge, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, UserOutlined, StarOutlined, UploadOutlined, TeamOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserGraduate, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import PasswordModal from './PasswordModal';
import EditStudentModal from './EditStudentModal';
import EditLinkedStudentForm from './EditLinkedStudentForm';

function EditDeleteStudent({ onCreateNewStudent }) {
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [studentIdToDelete, setStudentIdToDelete] = useState(null);
  const [studentIdToEdit, setStudentIdToEdit] = useState(null);
  const [passwordError, setPasswordError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [nameSearch, setNameSearch] = useState('');
  const [nicknameSearch, setNicknameSearch] = useState('');
  const [phoneSearch, setPhoneSearch] = useState('');
  const [isLinkedStudentModalOpen, setIsLinkedStudentModalOpen] = useState(false);
  const [schoolIdSearch, setSchoolIdSearch] = useState('');
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
const [nicknameTerm, setNicknameTerm] = useState('');
const [phoneTerm, setPhoneTerm] = useState('');
const [schoolIdTerm, setSchoolIdTerm] = useState('');
  const rowsPerPage = 100;
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;



const fetchStudents = async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem('token');
    
    // Combine name and nickname searches since backend handles both
    let combinedNameSearch = '';
    if (nameSearch && nicknameSearch) {
      combinedNameSearch = nameSearch; // prioritize name search
    } else {
      combinedNameSearch = nameSearch || nicknameSearch; // use whichever is provided
    }
    
    const response = await axios.get(`${API_URL}/students`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        page: currentPage,
        size: rowsPerPage,
        name: combinedNameSearch,
        phoneNumber: phoneSearch,
        schoolId: schoolIdSearch,
        sortBy: 'schoolid',    
        sortOrder: 'desc'      
      },
    });


    setStudents(response.data.students); 
    setTotalItems(response.data.totalItems);
    setTotalPages(response.data.totalPages);
  } catch (error) {
    console.error('Error fetching students:', error);
    setError(t('fetchStudentsError'));
  } finally {
    setLoading(false);
  }
};
useEffect(() => {
  fetchStudents();
}, [currentPage, nameSearch, nicknameSearch, phoneSearch, schoolIdSearch]);


const refreshStudentData = () => {
  // Call whatever function you use to refresh your student list
  // This could be fetchStudents, loadStudents, or whatever you named it
  fetchStudents(); // Replace with your actual function name
};

const handleSearch = () => {
  setNameSearch(searchTerm);
  setNicknameSearch(nicknameTerm);
  setPhoneSearch(phoneTerm);
  setSchoolIdSearch(schoolIdTerm);
  setCurrentPage(1);
};

const handleClearSearch = () => {
  setSearchTerm('');
  setNicknameTerm('');
  setPhoneTerm('');
  setSchoolIdTerm('');
  setNameSearch('');
  setNicknameSearch('');
  setPhoneSearch('');
  setSchoolIdSearch('');
  setCurrentPage(1);
};
// Filter students whenever filters or students change
useEffect(() => {
  const filtered = students.filter((student) => {
    const matchesName = nameSearch
      ? (student.name ?? `${student.firstname} ${student.lastname}`)
          .toLowerCase()
          .includes(nameSearch.toLowerCase())
      : true;

    const matchesNickname = nicknameSearch
      ? student.nickname?.toLowerCase().includes(nicknameSearch.toLowerCase())
      : true;

    const matchesPhone = phoneSearch
      ? student.phone?.includes(phoneSearch)
      : true;

    const matchesSchoolId = schoolIdSearch
      ? student.schoolid?.includes(schoolIdSearch)
      : true;

    return matchesName && matchesNickname && matchesPhone && matchesSchoolId;
  });

  setFilteredStudents(filtered);
}, [nameSearch, nicknameSearch, phoneSearch, schoolIdSearch, students]);


  const handleEdit = (studentId) => {
    setStudentIdToEdit(studentId);
    setIsEditModalOpen(true);
  };

  const handleEditLinkedStudent = (studentId) => {
  setStudentIdToEdit(studentId); // Set the main student ID
  setIsLinkedStudentModalOpen(true); // Open the linked students modal
};


  const handleDelete = (studentId) => {
    setStudentIdToDelete(studentId);
    setPasswordError('');
    setIsPasswordModalOpen(true);
  };

  const confirmDelete = async (password) => {
    setIsPasswordModalOpen(false);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError(t('noTokenFound'));
        return;
      }
      await axios.delete(`${API_URL}/students/${studentIdToDelete}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { password },
      });
      setStudents(prev => prev.filter(student => student.userid !== studentIdToDelete));

      Modal.success({ title: t('success'), content: t('studentDeleted') });
    } catch (error) {
      console.error('Error deleting student:', error);
      if (error.response && error.response.status === 401) {
        setPasswordError(t('invalidPassword'));
        setIsPasswordModalOpen(true);
      } else {
        setError(t('deleteStudentError'));
      }
    }
  };

  const saveStudent = async (updatedStudent) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/students/${updatedStudent.userid}`, updatedStudent, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStudents(prev => prev.map(student => (student.userid === updatedStudent.userid ? updatedStudent : student)));
      setIsEditModalOpen(false);

      Modal.success({ title: t('success'), content: t('studentUpdated') });
    } catch (error) {
      console.error('Error updating student:', error);
      setError(t('updateStudentError'));
    }
  };


  const handleNationalIdUpload = async (studentId) => {
    // Create a modal for file upload
    let fileToUpload = null;
  
    Modal.confirm({
      title: t('Upload National ID'),
      content: (
        <Upload
          beforeUpload={(file) => {
            // Only accept PDF, JPG, JPEG, and PNG files
            const isValidType = file.type === 'application/pdf' || 
                                file.type === 'image/jpeg' || 
                                file.type === 'image/jpg' || 
                                file.type === 'image/png';
            if (!isValidType) {
              message.error(t('onlyPdfAndImagesAllowed'));
              return Upload.LIST_IGNORE;
            }
            
            // Store the file for later use
            fileToUpload = file;
            return false; // Prevent auto upload
          }}
          maxCount={1}
          accept=".pdf,.jpg,.jpeg,.png"
        >
          <Button icon={<UploadOutlined />}>{t('Select File')}</Button>
        </Upload>
      ),
      onOk: async () => {
        if (!fileToUpload) {
          message.error(t('Please Select File'));
          return;
        }
  
        try {
          const token = localStorage.getItem('token');
          const formData = new FormData();
          formData.append('nationalIdFile', fileToUpload); // Make sure this field name matches
          formData.append('userId', studentId);
      
          // Fix the endpoint URL
          await axios.post(`${API_URL}/upload/national_ids/upload`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${token}`
            }
          });
  
          message.success(t('National ID Uploaded Successfully'));
          
          // Refresh the student list to show the new file
          const queryParams = new URLSearchParams({
            page: String(currentPage),
            name: nameSearch,
            phoneNumber: phoneSearch,
            schoolId: schoolIdSearch,
          });
  
          const response = await axios.get(`${API_URL}/students?${queryParams.toString()}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
  
          setStudents(response.data.students);
        } catch (error) {
          console.error('Error uploading national ID:', error);
          message.error(t('Upload Failed.'));
        }
      },
      onCancel: () => {}
    });
  };
  
  const nationalIdColumn = {
    title: t('nationalId'),
    dataIndex: 'national_id_pdf',
    render: (pdf, student) => (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {pdf ? (
          <>
            <a 
              href={`${API_URL}/upload/national_ids/${pdf.split('/').pop()}`} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ marginRight: '8px' }}
            >
              <FontAwesomeIcon icon={faFilePdf} style={{ color: 'red' }} />
            </a>
            
            {/* Option to replace the file */}
            <Button 
              size="small" 
              type="text" 
              icon={<UploadOutlined />} 
              onClick={(e) => {
                e.stopPropagation();
                handleNationalIdUpload(student.userid);
              }}
              title={t('replaceFile')}
            />
          </>
        ) : (
          <Button 
            size="small" 
            icon={<UploadOutlined />} 
            onClick={(e) => {
              e.stopPropagation();
              handleNationalIdUpload(student.userid);
            }}
          >
          </Button>
        )}
      </div>
    ),
  };

  const viewStudentDashboard = (studentId) => {
    navigate(`/student-dashboard-admin/${studentId}`);
  };

  const viewStudentPerformance = (studentId) => {
    navigate(`/studentperformance/${studentId}`);
  };

  const columns = [
    {
      title: '',
      dataIndex: 'dashboard',
      render: (_, student) => (
        <Button shape="circle"
                      icon={<UserOutlined />} onClick={() => viewStudentDashboard(student.userid)} />
      ),
    },
    {
      title: '',
      dataIndex: 'performance',
      render: (_, student) => (
        <Button shape="circle"
        icon={<StarOutlined />} onClick={() => viewStudentPerformance(student.userid)} />
      ),
    },
    { 
      title: t('id'), 
      dataIndex: 'schoolid'
    },
    { title: t('name'), dataIndex: 'name', render: (_, student) => `${student.firstname} ${student.lastname}` },
    { title: t('nickname'), dataIndex: 'nickname' },
    { title: t('age'), dataIndex: 'age' },
 {
  title: t('level'),
  dataIndex: 'currenteducationlevel',
  render: (level) => {
    if (!level) return '-'; // Handle null/undefined values
    return level; // Just return the level as-is
  }
},
    { title: t('school'), dataIndex: 'schoolname' },
 {
  title: t('Sales'),
  dataIndex: 'salesperson_nickname',
  key: 'salesperson_nickname',
  render: (nickname) =>
    nickname ? (
      <Tag color="blue">{nickname}</Tag>
    ) : (
      <Tag color="default">{t('No Sales')}</Tag>
    ),
},

    nationalIdColumn,
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const isActive = status?.toLowerCase() === 'active';
        return (
          <Tag color={isActive ? 'green' : 'red'}>
            {isActive ? t('Active') : t('Inactive')}
          </Tag>
        );
      }
    },
    {
      title: t('created at'),
      dataIndex: 'createdat',
      key: 'createdat',
      render: (date) => {
        if (!date) return '-';
        return new Intl.DateTimeFormat('en-GB', {
          day: '2-digit',
          month: 'short',
          year: '2-digit',
        }).format(new Date(date));
      }
    },
     {
          title: t('actions'),
          dataIndex: 'actions',
          render: (_, student) => (
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                type="primary"
                shape="circle"
                icon={<EditOutlined />}
                onClick={() => handleEdit(student.userid)}
              />
              <Button
                shape="circle"
                type="primary"
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(student.userid)}
              />
              <Badge 
        count={student.linked_students?.length || 0}
        showZero={false}
        style={{ backgroundColor: '#52c41a' }}
      >
        <Button
          shape="circle"
          type="default"
          icon={<TeamOutlined />}
          title={t('editLinkedStudent')}
          onClick={() => handleEditLinkedStudent(student.userid)}
        />
      </Badge>
            </div>
          ),
        }
  ];

  if (loading) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
      <Spin tip={t('loading')} size="large" />
    </div>
  );
}

  if (error) return <p>{error}</p>;

  return (
    <div>
      <Button type="primary" icon={<FontAwesomeIcon icon={faUserGraduate} />} onClick={onCreateNewStudent}>
        {t('createNewStudent')}
      </Button>
      <div style={{ marginTop: 16, marginBottom: 16 }}>
  <Input
    placeholder={t('searchByName')}
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
    onPressEnter={handleSearch}
    style={{ marginBottom: 8, width: 200, marginRight: 16 }}
  />
  <Input
    placeholder={t('Search by Nickname')}
    value={nicknameTerm}
    onChange={(e) => setNicknameTerm(e.target.value)}
    onPressEnter={handleSearch}
    style={{ marginBottom: 8, width: 200, marginRight: 16 }}
  />
  <Input
    placeholder={t('searchByPhone')}
    value={phoneTerm}
    onChange={(e) => setPhoneTerm(e.target.value)}
    onPressEnter={handleSearch}
    style={{ marginBottom: 8, width: 200, marginRight: 16 }}
  />
  <Input
    placeholder={t('searchBySchoolID')}
    value={schoolIdTerm}
    onChange={(e) => setSchoolIdTerm(e.target.value)}
    onPressEnter={handleSearch}
    style={{ marginBottom: 8, width: 200, marginRight: 16 }}
  />
  <Button type="primary" onClick={handleSearch} style={{ marginRight: 8 }}>
    {t('Search')}
  </Button>
  <Button onClick={handleClearSearch}>
    {t('Clear')}
  </Button>
</div>
        <Table
          columns={columns}
          dataSource={filteredStudents} // Use students directly from server
          rowKey="userid"
          pagination={{
            current: currentPage,
            pageSize: rowsPerPage,
            total: totalItems, // Use server's totalItems
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: false,
          }}
          scroll={{ x: 'max-content' }}
        />
      <PasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onConfirm={confirmDelete}
        errorMessage={passwordError}
      />
      <EditStudentModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={saveStudent}
        studentId={studentIdToEdit}
      />

      <EditLinkedStudentForm
  visible={isLinkedStudentModalOpen}
  onClose={() => setIsLinkedStudentModalOpen(false)}
  mainUserId={studentIdToEdit} // Use the stored student ID
  onUpdated={refreshStudentData}
/>
    </div>
  );
}

export default withRoleAccess(EditDeleteStudent, ['admin', 'superadmin']);
